import { Box, Slider } from "@mui/material"
import { SystemCssProperties } from "@mui/system"
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation"
import React from "react"
import useTranslate, { Namespaces } from "src/hooks/use-translate"

import { DGText } from "../../../components/common/DGText"
import { TranslationKey } from "../../../types"
import { comb, reReadAriaLabel, s } from "../../../utilities/common"
import { theme, TI } from "../../../utilities/theme"

const options: TranslationKey<Namespaces.Default>[] = [
  "settings_dynamic_type_option_small",
  "settings_dynamic_type_option_medium",
  "settings_dynamic_type_option_large",
  "settings_dynamic_type_option_larger",
  "settings_dynamic_type_option_largest",
] as const

export const SliderBar = ({
  onChange,
  value,
}: {
  onChange: (
    event: Event,
    value: number | number[],
    activeThumb: number,
  ) => void
  value: comb<number>
}) => {
  const { ref, focused, focusSelf } = useFocusable({
    onArrowPress: (direction) => {
      if (direction === "left") {
        value.set(Math.max(1, value.val - 1))
      } else if (direction === "right") {
        value.set(Math.min(5, value.val + 1))
      }
      reReadAriaLabel(ref)
      return true
    },
  })
  const t = useTranslate()
  return (
    <Box
      ref={ref}
      tabIndex={0}
      aria-label={
        t("settings_menu_option_dynamic_type") +
        ": " +
        t(options[value.val - 1])
      }
      onMouseEnter={() => focusSelf({ noScroll: true })}
      sx={{
        display: "flex",
        flexDirection: "column",
        borderTop: "1px solid",
        borderBottom: "1px solid",
        borderColor: focused ? "transparent" : theme.palette.white[20],
        p: s("40px"),
        borderRadius: focused ? s("16px") : undefined,
        transform: focused ? "scale(1.14)" : undefined,
        backgroundColor: focused ? theme.palette.secondary.variant : undefined,
        boxShadow: focused
          ? `${s("0px 30px 112px 0px")} rgba(0, 0, 0, 0.6)`
          : undefined,
        pb: s("70px"),
        pl: s("70px"),
        pr: s("70px"),
      }}
    >
      <DGText
        tx={TI.Regular04tv}
        sx={{ mb: s("10px"), color: theme.palette.white.main }}
      >
        {t("settings_menu_option_dynamic_type")}
      </DGText>
      <Slider
        onChange={onChange}
        value={value.val}
        valueLabelDisplay="auto"
        shiftStep={1}
        step={1}
        marks={options.map((option, i) => ({
          value: i + 1,
          label: t(option),
        }))}
        min={1}
        max={5}
        sx={{
          color: theme.palette.white["55"],
          height: s(5),
          padding: "15px 0",
          "& .MuiSlider-thumb": {
            height: s(40),
            width: s(40),
            backgroundColor: theme.palette.white.main,
            boxShadow: "0 0 2px 0px rgba(0, 0, 0, 0.1)",
            "&:focus, &:hover, &.Mui-active": {
              boxShadow: "0px 0px 3px 1px rgba(0, 0, 0, 0.1)",
            },
            "&:before": {
              boxShadow:
                "0px 0px 1px 0px rgba(0,0,0,0.2), 0px 0px 0px 0px rgba(0,0,0,0.14), 0px 0px 1px 0px rgba(0,0,0,0.12)",
            },
          },
          "& .MuiSlider-valueLabel": {
            display: "none",
          },
          "& .MuiSlider-track": {
            border: "none",
            height: s(20),
          },
          "& .MuiSlider-rail": {
            opacity: 0.5,
            boxShadow: "inset 0px 0px 4px -2px #000",
            backgroundColor: theme.palette.white["70"],
            height: s(20),
          },
          "& .MuiSlider-mark": {
            height: s(10),
            width: s(10),
            borderRadius: "50%",
          },
          // Hide only marks for specific values
          '& .MuiSlider-mark[data-index="0"], .MuiSlider-mark[data-index="4"]':
            {
              display: "none",
            },
          "& .MuiSlider-markLabel": {
            top: s(40),
            color: theme.palette.white.main,
            ...(theme.typography[TI.Regular04tv] as SystemCssProperties),
          },
        }}
      />
    </Box>
  )
}
